import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { FS_XS, COLOR_PRIMARY } from '../../common/theme'
import type { ToolEvent } from '../../types'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toLabel(v: unknown): string {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>
    // Common shape from backend: { Method, Target }
    if ('Method' in obj || 'Target' in obj) {
      return [obj.Method, obj.Target].filter(Boolean).map(x => toLabel(x)).join(': ')
    }
    try { return JSON.stringify(v) } catch { return String(v) }
  }
  return String(v)
}

// ─── Tool timeline ───────────────────────────────────────────────────────────

export function ToolTimeline({ tools, connectsToBubble, connectsAbove }: { tools: ToolEvent[]; connectsToBubble?: boolean; connectsAbove?: boolean }) {
  if (!tools.length) return null
  return (
    <Box data-testid="chat_tool_timeline" sx={{ mb: connectsToBubble ? 0 : 1.25 }}>
      {tools.map((t, i) => {
        const isLast = i === tools.length - 1
        const statusLabel = toLabel(t.status)
        const isRunning = statusLabel !== 'complete' && statusLabel !== 'done' && statusLabel !== 'error'
        const dotColor = statusLabel === 'error' ? '#e05c5c' : isRunning ? '#1976d2' : '#43a047'
        const topLineVisible = i > 0 || !!connectsAbove
        return (
          <Box key={i} data-testid="chat_tool_row" data-tool={t.tool} data-status={statusLabel} sx={{ display: 'flex', alignItems: 'stretch', minHeight: 28 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
              <Box sx={{ width: 1.5, flex: '0 0 8px', bgcolor: topLineVisible ? 'rgba(0,0,0,0.15)' : 'transparent' }} />
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0, boxShadow: isRunning ? `0 0 0 3px ${dotColor}30` : 'none' }} />
              <Box sx={{ width: 1.5, flex: 1, minHeight: 8, bgcolor: (isLast && !connectsToBubble) ? 'transparent' : 'rgba(0,0,0,0.15)' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', pl: 0.5, pb: isLast ? 0 : 0.25 }}>
              <Typography sx={{ fontSize: FS_XS, fontFamily: 'monospace', lineHeight: 1.4, fontWeight: 600, color: 'text.primary' }}>{toLabel(t.tool)}</Typography>
              <Typography sx={{ fontSize: '0.67rem', color: 'text.secondary', lineHeight: 1.4, fontStyle: 'italic' }}>{statusLabel}</Typography>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

// ─── Connector between timeline and bubble ──────────────────────────────────

export function ToolBubbleConnector() {
  return <Box sx={{ ml: '9px', width: 1.5, height: 8, bgcolor: 'rgba(0,0,0,0.15)' }} />
}

// ─── Bouncing-dots typing indicator ──────────────────────────────────────────

export function BouncingDots() {
  return (
    <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center', py: 0.75, px: 0.25 }}>
      {[0, 1, 2].map(i => (
        <Box key={i} sx={{
          width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.secondary',
          animation: 'dotBounce 1.2s ease-in-out infinite',
          animationDelay: `${i * 0.2}s`,
          '@keyframes dotBounce': {
            '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
            '40%': { transform: 'scale(1)', opacity: 1 },
          },
        }} />
      ))}
    </Box>
  )
}

// ─── Thinking block ──────────────────────────────────────────────────────────

export function ThinkingBlock({ thinking, defaultOpen = false, connectsAbove = false, connectsBelow = false }: { thinking: string; defaultOpen?: boolean; connectsAbove?: boolean; connectsBelow?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  if (!thinking || !thinking.trim()) return null
  const dotColor = COLOR_PRIMARY
  return (
    <Box
      data-testid="chat_thinking_block"
      onClick={() => setOpen(o => !o)}
      sx={{ display: 'flex', alignItems: 'stretch', minHeight: 28, cursor: 'pointer' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
        <Box sx={{ width: 1.5, flex: '0 0 8px', bgcolor: connectsAbove ? 'rgba(0,0,0,0.15)' : 'transparent' }} />
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
        <Box sx={{ width: 1.5, flex: 1, minHeight: 8, bgcolor: connectsBelow ? 'rgba(0,0,0,0.15)' : 'transparent' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', pl: 0.5, flex: 1, minWidth: 0, pb: 0.25 }}>
        {(open || true) && (
          <Typography sx={{
            display: 'block',
            fontSize: '0.67rem', fontStyle: 'italic',
            color: 'text.secondary', whiteSpace: 'pre-wrap',
            mt: 0.25, mr: 1,
          }}>
            {thinking}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

